import { useState } from 'react';
import {
  FormInput,
  Table2,
  Image,
  CheckSquare,
  Calendar,
  PenLine,
  User,
  Mail,
  Phone,
  Rows3,
  Plus,
  Minus,
  LayoutGrid,
  Sparkles,
  Wrench,
} from 'lucide-react';
import { ShieldCheck, Layers, ScrollText, CircleDot } from 'lucide-react';
const ICON_COLORS = {
  field: 'bg-[#EEF1FD] text-[#375AE7] ring-[#375AE7]/15',
  table: 'bg-sky-50 text-sky-600 ring-sky-100',
  image: 'bg-violet-50 text-violet-600 ring-violet-100',
  checkbox: 'bg-emerald-50 text-emerald-600 ring-emerald-100',
  radio: 'bg-indigo-50 text-indigo-600 ring-indigo-100',
  date: 'bg-amber-50 text-amber-600 ring-amber-100',
  signature: 'bg-rose-50 text-rose-600 ring-rose-100',
  name: 'bg-teal-50 text-teal-600 ring-teal-100',
  email: 'bg-blue-50 text-blue-600 ring-blue-100',
  phone: 'bg-cyan-50 text-cyan-600 ring-cyan-100',
};

function ToolboxSection({ title, icon: Icon, children, variant = 'default' }) {
  const isHighlighted = variant === 'highlighted';

  return (
    <section
      className={`rounded-xl border ${
        isHighlighted
          ? 'border-[#375AE7]/20 bg-gradient-to-br from-[#EEF1FD]/80 via-white to-[#F5F7FE]/60 shadow-sm'
          : 'border-slate-200/80 bg-white shadow-sm'
      }`}
    >
      <div
        className={`flex items-center gap-2 px-3 py-2.5 border-b ${
          isHighlighted ? 'border-[#375AE7]/10' : 'border-slate-100'
        }`}
      >
        {Icon && (
          <span
            className={`flex h-6 w-6 items-center justify-center rounded-md ${
              isHighlighted
                ? 'bg-[#375AE7]/10 text-[#375AE7]'
                : 'bg-slate-100 text-slate-500'
            }`}
          >
            <Icon className="h-3.5 w-3.5" strokeWidth={2.25} />
          </span>
        )}
        <h3
          className={`text-[11px] font-semibold uppercase tracking-wider ${
            isHighlighted ? 'text-[#375AE7]' : 'text-slate-500'
          }`}
        >
          {title}
        </h3>
      </div>
      <div className="p-2.5 space-y-1">{children}</div>
    </section>
  );
}

function IconBadge({ icon: Icon, colorKey }) {
  return (
    <span
      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ring-1 ${ICON_COLORS[colorKey]}`}
    >
      <Icon className="h-4 w-4" strokeWidth={2} />
    </span>
  );
}

function Item({ icon: Icon, colorKey, label, onClick, draggable, dragData }) {
  return (
    <button
      type="button"
      onClick={onClick}
      draggable={!!draggable}
      onDragStart={(e) => {
        if (!draggable || !dragData) return;
        try {
          const payload = JSON.stringify(dragData);
          e.dataTransfer.setData('application/x-rp-tool', payload);
          e.dataTransfer.setData('text/plain', payload);
          e.dataTransfer.effectAllowed = 'copyMove';
          console.debug('[Toolbox] dragstart', dragData);
        } catch (err) {
          console.log('error', err);
        }
      }}
      className="group flex w-full items-center gap-3 rounded-lg border border-transparent px-2.5 py-2 text-left transition-all duration-150 hover:border-[#375AE7]/15 hover:bg-[#375AE7]/5 active:scale-[0.98]"
    >
      <IconBadge icon={Icon} colorKey={colorKey} />
      <span className="text-sm font-medium text-slate-700 group-hover:text-slate-900">
        {label}
      </span>
      {draggable && (
        <span className="ml-auto text-[10px] font-medium uppercase tracking-wide text-slate-300 opacity-0 transition-opacity group-hover:opacity-100">
          Drag
        </span>
      )}
    </button>
  );
}

function ToolButton({ title, onClick, children, className = '' }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className={`inline-flex items-center justify-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-600 shadow-sm transition-all hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 active:scale-[0.97] ${className}`}
    >
      {children}
    </button>
  );
}

export default function Toolbox({ editor }) {
  const [showModal, setShowModal] = useState(false);
  const [placeholderData, setPlaceholderData] = useState({
    key: '',
    label: '',
  });
  const [tableModal, setTableModal] = useState(false);
  const [tableConfig, setTableConfig] = useState({
    rows: 2,
    cols: 2,
    withHeader: false,
  });
  const [radioModal, setRadioModal] = useState(false);
  const [radioConfig, setRadioConfig] = useState({
    label: 'Select one',
    options: ['Option 1', 'Option 2'],
  });
  return (
    <aside className="rp-toolbox flex h-full w-full flex-col bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <div className=" border-b border-slate-200/80 bg-white px-4 py-4">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#375AE7] text-white shadow-md shadow-[#375AE7]/30">
            <Wrench className="h-5 w-5" strokeWidth={2.25} />
          </span>
          <div>
            <h2 className="text-sm font-bold tracking-tight text-slate-800">
              Toolbox
            </h2>
            <p className="text-[11px] text-slate-500">
              Insert & format document blocks
            </p>
          </div>
        </div>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 space-y-3 overflow-y-auto p-3 sm:p-4">
        {/* Insert Blocks */}
        <ToolboxSection title="Insert Blocks" icon={LayoutGrid}>
          <Item
            icon={FormInput}
            colorKey="field"
            label="Add Field"
            onClick={() => setShowModal(true)}
            draggable
            dragData={{ type: 'placeholder', key: 'field', label: 'Field' }}
          />
          <Item
            icon={Table2}
            colorKey="table"
            label="Table"
            onClick={() => setTableModal(true)}
            draggable
            dragData={{ type: 'table', rows: 2, cols: 2 }}
          />
          <Item
            icon={Image}
            colorKey="image"
            label="Image"
            onClick={() => document.getElementById('tb-image-input').click()}
            draggable
            dragData={{ type: 'image' }}
          />
          <Item
            icon={CheckSquare}
            colorKey="checkbox"
            label="Checkbox"
            onClick={() => editor.chain().focus().insertCheckbox().run()}
            draggable
            dragData={{ type: 'checkbox' }}
          />
          <Item
            icon={CircleDot}
            colorKey="radio"
            label="Radio Group"
            onClick={() => setRadioModal(true)}
            draggable
            dragData={{
              type: 'radio-group',
              preview: radioConfig,
            }}
          />
          <Item
            icon={Calendar}
            colorKey="date"
            label="Date"
            onClick={() => editor.chain().focus().insertDate().run()}
            draggable
            dragData={{ type: 'date' }}
          />
          <Item
            icon={PenLine}
            colorKey="signature"
            label="Signature"
            onClick={() => editor.chain().focus().insertSignature().run()}
            draggable
            dragData={{ type: 'signature' }}
          />
        </ToolboxSection>

        {/* Table Tools */}
        <ToolboxSection title="Table Tools" icon={Table2}>
          <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3">
            <ToolButton
              title="Add row after"
              onClick={() => editor.chain().focus().addRowAfter().run()}
            >
              <Plus className="h-3 w-3 text-emerald-600" />
              Row
            </ToolButton>
            <ToolButton
              title="Delete row"
              onClick={() => editor.chain().focus().deleteRow().run()}
            >
              <Minus className="h-3 w-3 text-rose-500" />
              Row
            </ToolButton>
            <ToolButton
              title="Add column after"
              onClick={() => editor.chain().focus().addColumnAfter().run()}
            >
              <Plus className="h-3 w-3 text-emerald-600" />
              Col
            </ToolButton>
            <ToolButton
              title="Delete column"
              onClick={() => editor.chain().focus().deleteColumn().run()}
            >
              <Minus className="h-3 w-3 text-rose-500" />
              Col
            </ToolButton>
            <ToolButton
              title="Toggle header"
              onClick={() => {
                try {
                  if (
                    editor &&
                    editor.commands &&
                    typeof editor.commands.toggleHeader === 'function'
                  ) {
                    editor.chain().focus().toggleHeader().run();
                  } else if (
                    editor &&
                    editor.commands &&
                    typeof editor.commands.toggleHeaderRow === 'function'
                  ) {
                    editor.chain().focus().toggleHeaderRow().run();
                  }
                } catch (e) {
                  console.log('error', e);
                }
              }}
              className="col-span-2 sm:col-span-1"
            >
              <Rows3 className="h-3 w-3 text-sky-600" />
              Header
            </ToolButton>
          </div>
        </ToolboxSection>

        {/* Typography moved to top toolbar; removed from side toolbox */}
        {radioModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
            <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
              <h2 className="text-lg font-semibold text-slate-800 mb-4">
                Create Radio Group
              </h2>

              {/* Label */}
              <input
                placeholder="Group Label (e.g Gender)"
                className="w-full mb-3 rounded-lg border px-3 py-2 text-sm"
                value={radioConfig.label}
                onChange={(e) =>
                  setRadioConfig({ ...radioConfig, label: e.target.value })
                }
              />

              {/* Options */}
              <div className="space-y-2">
                {radioConfig.options.map((opt, i) => (
                  <div key={i} className="flex gap-2">
                    <input
                      value={opt}
                      onChange={(e) => {
                        const updated = [...radioConfig.options];
                        updated[i] = e.target.value;
                        setRadioConfig({ ...radioConfig, options: updated });
                      }}
                      className="flex-1 rounded-lg border px-3 py-2 text-sm"
                    />
                    <button
                      onClick={() => {
                        const updated = radioConfig.options.filter(
                          (_, idx) => idx !== i,
                        );
                        setRadioConfig({ ...radioConfig, options: updated });
                      }}
                      className="text-red-500"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>

              <button
                onClick={() =>
                  setRadioConfig({
                    ...radioConfig,
                    options: [
                      ...radioConfig.options,
                      `Option ${radioConfig.options.length + 1}`,
                    ],
                  })
                }
                className="mt-3 text-sm text-[#375AE7]"
              >
                + Add Option
              </button>

              {/* Actions */}
              <div className="flex justify-end gap-2 mt-5">
                <button onClick={() => setRadioModal(false)}>Cancel</button>

                <button
                  onClick={() => {
                    editor
                      .chain()
                      .focus()
                      .insertRadioGroup({
                        label: radioConfig.label,
                        options: radioConfig.options,
                        selected: null,
                      })
                      .run();
                    setRadioModal(false);
                  }}
                  className="bg-[#375AE7] text-white px-4 py-2 rounded-lg"
                >
                  Insert
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Default Fields — separate highlighted section */}
        <ToolboxSection
          title="Default Fields"
          icon={Sparkles}
          variant="highlighted"
        >
          <p className="mb-1.5 px-1 text-[11px] leading-relaxed text-[#375AE7]/70">
            Quick-insert common placeholders
          </p>
          <Item
            icon={User}
            colorKey="name"
            label="Name"
            onClick={() =>
              editor
                .chain()
                .focus()
                .insertPlaceholder({
                  id: crypto.randomUUID(),
                  key: 'name',
                  label: 'Name',
                  value: '',
                })
                .run()
            }
            draggable
            dragData={{ type: 'placeholder', key: 'name', label: 'Name' }}
          />
          <Item
            icon={Mail}
            colorKey="email"
            label="Email"
            onClick={() =>
              editor
                .chain()
                .focus()
                .insertPlaceholder({
                  id: crypto.randomUUID(),
                  key: 'email',
                  label: 'Email',
                  value: '',
                })
                .run()
            }
            draggable
            dragData={{ type: 'placeholder', key: 'email', label: 'Email' }}
          />
          <Item
            icon={Phone}
            colorKey="phone"
            label="Phone"
            onClick={() =>
              editor
                .chain()
                .focus()
                .insertPlaceholder({
                  id: crypto.randomUUID(),
                  key: 'phone',
                  label: 'Phone',
                  value: '',
                })
                .run()
            }
            draggable
            dragData={{ type: 'placeholder', key: 'phone', label: 'Phone' }}
          />
        </ToolboxSection>
        <ToolboxSection title="Annexures" icon={Layers}>
          <p className="mb-1.5 px-1 text-[11px] text-slate-500">
            Attach supporting documents & references
          </p>

          <Item
            icon={ShieldCheck}
            colorKey="checkbox"
            label="Special Conditions"
            draggable
            dragData={{ type: 'specialConditions' }}
            onClick={() =>
              editor.chain().focus().insertSpecialConditions().run()
            }
          />

          <Item
            icon={ScrollText}
            colorKey="image"
            label="Reference Annexure"
            draggable
            dragData={{ type: 'annexure', label: 'Reference Annexure' }}
            onClick={() =>
              editor
                .chain()
                .focus()
                .insertContent({
                  type: 'paragraph',
                  content: [
                    { type: 'text', text: 'Refer to Annexure for details...' },
                  ],
                })
                .run()
            }
          />
        </ToolboxSection>
      </div>

      <input
        id="tb-image-input"
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (!file) return;
          const reader = new FileReader();
          reader.onload = (ev) => {
            const src = ev.target.result;
            editor.chain().focus().setImage({ src }).run();
          };
          reader.readAsDataURL(file);
          e.target.value = null;
        }}
      />

      {/* Table Modal */}
      {tableModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-[420px] rounded-2xl border border-slate-200 bg-white p-5 shadow-2xl">
            <div className="mb-4 flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-sky-50 text-sky-600 ring-1 ring-sky-100">
                <Table2 className="h-4 w-4" />
              </span>
              <h2 className="text-lg font-semibold text-slate-800">
                Insert Table
              </h2>
            </div>
            <div className="mb-3 grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-500">
                  Rows
                </label>
                <input
                  type="number"
                  min={1}
                  value={tableConfig.rows}
                  onChange={(e) =>
                    setTableConfig({
                      ...tableConfig,
                      rows: Math.max(1, parseInt(e.target.value || 1)),
                    })
                  }
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-[#375AE7]/50 focus:outline-none focus:ring-2 focus:ring-[#375AE7]/15"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-500">
                  Columns
                </label>
                <input
                  type="number"
                  min={1}
                  value={tableConfig.cols}
                  onChange={(e) =>
                    setTableConfig({
                      ...tableConfig,
                      cols: Math.max(1, parseInt(e.target.value || 1)),
                    })
                  }
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-[#375AE7]/50 focus:outline-none focus:ring-2 focus:ring-[#375AE7]/15"
                />
              </div>
            </div>
            <div className="mb-5 flex items-center gap-2">
              <input
                id="tb-header"
                type="checkbox"
                checked={tableConfig.withHeader}
                onChange={(e) =>
                  setTableConfig({
                    ...tableConfig,
                    withHeader: e.target.checked,
                  })
                }
                className="h-4 w-4 rounded border-slate-300 text-[#375AE7] focus:ring-[#375AE7]/30"
              />
              <label htmlFor="tb-header" className="text-sm text-slate-600">
                Include header row
              </label>
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setTableModal(false)}
                className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  const { rows, cols, withHeader } = tableConfig;
                  editor
                    .chain()
                    .focus()
                    .insertTable({ rows, cols, withHeaderRow: withHeader })
                    .run();
                  setTableModal(false);
                  setTableConfig({ rows: 2, cols: 2, withHeader: false });
                }}
                className="rounded-lg bg-[#375AE7] px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-[#2E4BC4]"
              >
                Insert
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Field Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-[420px] rounded-2xl border border-slate-200 bg-white p-5 shadow-2xl">
            <div className="mb-4 flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#EEF1FD] text-[#375AE7] ring-1 ring-[#375AE7]/15">
                <FormInput className="h-4 w-4" />
              </span>
              <h2 className="text-lg font-semibold text-slate-800">
                Add Placeholder Field
              </h2>
            </div>

            <label className="mb-1 block text-xs font-medium text-slate-500">
              Variable Key
            </label>
            <input
              className="mb-3 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-[#375AE7]/50 focus:outline-none focus:ring-2 focus:ring-[#375AE7]/15"
              placeholder="e.g. email, fullName, address"
              value={placeholderData.key}
              onChange={(e) =>
                setPlaceholderData({ ...placeholderData, key: e.target.value })
              }
            />

            <label className="mb-1 block text-xs font-medium text-slate-500">
              Display Label
            </label>
            <input
              className="mb-5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-[#375AE7]/50 focus:outline-none focus:ring-2 focus:ring-[#375AE7]/15"
              placeholder="e.g. Enter your email"
              value={placeholderData.label}
              onChange={(e) =>
                setPlaceholderData({
                  ...placeholderData,
                  label: e.target.value,
                })
              }
            />

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  if (!placeholderData.key) return;

                  editor
                    .chain()
                    .focus()
                    .insertPlaceholder({
                      id: crypto.randomUUID(),
                      key: placeholderData.key,
                      label:
                        placeholderData.label || `Enter ${placeholderData.key}`,
                      value: '',
                    })
                    .run();

                  setShowModal(false);
                  setPlaceholderData({ key: '', label: '' });
                }}
                className="rounded-lg bg-[#375AE7] px-4 py-2 text-sm font-medium text-white shadow-md shadow-[#375AE7]/25 transition hover:bg-[#2E4BC4]"
              >
                Insert Field
              </button>
            </div>
          </div>
        </div>
      )}
    </aside>
  );
}
